import { useEffect, useState } from 'react';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { IconAction } from '@/components/ui/icon-action';
import { clientApi } from '@/config/axios';
import { Field, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { apiError, useImagingApi } from './api';

export function AttachmentViewer({ studyId, attachments }: {
  studyId: string; attachments: { id: string; filename: string; content_type: string }[];
}) {
  const { base } = useImagingApi();
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState('');
  const [preview, setPreview] = useState<{ id: string; url: string }>();
  const [error, setError] = useState('');
  const file = attachments.find(a => a.id === selected) || attachments[0];
  const index = attachments.findIndex(a => a.id === file?.id);
  useEffect(() => {
    if (!open || !file) return;
    let active = true;
    let url = '';
    const abort = new AbortController();
    setPreview(undefined);
    setError('');
    clientApi.get(`${base}/studies/${studyId}/attachments/${file.id}/content`, {
      responseType: 'blob', signal: abort.signal,
    }).then(result => {
      if (!active) return;
      url = URL.createObjectURL(result.data);
      setPreview({ id: file.id, url });
    }).catch(e => { if (active) setError(apiError(e)); });
    return () => { active = false; abort.abort(); if (url) URL.revokeObjectURL(url); };
  }, [base, studyId, file?.id, open]);
  if (!attachments.length) return null;
  return <>
    <Button variant="outline" onClick={() => setOpen(true)}>View JPG/PDF ({attachments.length})</Button>
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Study attachments</DialogTitle></DialogHeader>
        <DialogDescription>Original JPG and PDF files. Run Extract full study to prepare these pages for OHIF and measurement review.</DialogDescription>
        <FieldGroup><Field><FieldLabel htmlFor="study-attachment">File</FieldLabel>
          <div className="flex items-center gap-3">
          <div className="min-w-0 flex-1 sm:flex-none sm:w-3/4">
          <Select value={file?.id || ''} onValueChange={setSelected}>
            <SelectTrigger id="study-attachment" className="w-full truncate"><SelectValue /></SelectTrigger>
            <SelectContent><SelectGroup>
              {attachments.map(a => <SelectItem key={a.id} value={a.id}>{a.filename}</SelectItem>)}
            </SelectGroup></SelectContent>
          </Select>
          </div>
          <div className="flex items-center gap-2">
            <IconAction variant="outline" icon={ArrowLeft} label="Previous file" disabled={index <= 0} onClick={() => setSelected(attachments[index - 1].id)} />
            <span className="whitespace-nowrap" aria-live="polite">{index + 1} / {attachments.length}</span>
            <IconAction variant="outline" icon={ArrowRight} label="Next file" disabled={index >= attachments.length - 1} onClick={() => setSelected(attachments[index + 1].id)} />
          </div>
          </div>
        </Field></FieldGroup>
        {error && <p role="alert">{error}</p>}
        {!error && preview?.id !== file?.id && <p role="status">Loading preview…</p>}
        {preview?.id === file?.id && (file.content_type === 'application/pdf'
          ? <iframe title={file.filename} src={preview.url} className="w-full h-[65vh]" />
          : <img alt={file.filename} src={preview.url} className="w-full max-h-[65vh] object-contain" />)}
      </DialogContent>
    </Dialog>
  </>;
}
